import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Video from 'twilio-video';
import { useVideoConstraints, useMediaDevices } from 'hooks';

export default function useLocalTracks(settings, onError) {
  const { t } = useTranslation('labels');
  const [audioTrack, setAudioTrack] = useState(null);
  const [videoTrack, setVideoTrack] = useState(null);
  const [dataTrack, setDataTrack] = useState(null);
  const supportedContraints = useMemo(() => window?.navigator?.mediaDevices?.getSupportedConstraints && window?.navigator?.mediaDevices?.getSupportedConstraints());

  const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
  const videoConstrains = useVideoConstraints();
  const { audioInputId, videoInputId, environmentVideoInputId, videoInputEnabled, audioInputEnabled } = settings;
  const {
    audioInputDevices,
    videoInputDevices,
    environmentalVideoInputDevices,
    hasAudioInputDevices,
    hasVideoInputDevices,
    devicesAvailability: { isCameraPermissionDenied, isMicrophonePermissionDenied },
  } = useMediaDevices();

  const getLocalDataTrack = useCallback(() => {
    const newTrack = new Video.LocalDataTrack({
      kind: 'data',
      maxPacketLifeTime: null,
      maxRetransmits: null,
    });
    setDataTrack(newTrack);
    return newTrack;
  });

  const getLocalAudioTrack = useCallback(
    (forceCreatedTrack = false) => {
      if (!audioInputEnabled && !forceCreatedTrack) {
        setAudioTrack(null);
        return Promise.resolve(null);
      }

      const hasSelectedAudioDevice = audioInputDevices.some((device) => audioInputId && device.value === audioInputId);
      const shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;

      const options = { ...(shouldAcquireAudio && (hasSelectedAudioDevice ? { deviceId: { exact: audioInputId } } : hasAudioInputDevices)) };
      if (supportedContraints.echoCancellation) {
        options.echoCancellation = true;
      }
      // not supported on Safari iOS
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings/noiseSuppression
      if (supportedContraints.noiseSuppression) {
        options.noiseSuppression = true;
      }

      return Video.createLocalAudioTrack(options)
        .then((newTrack) => {
          if (!audioInputEnabled) {
            newTrack.disable();
          }
          setAudioTrack(newTrack);
          return newTrack;
        })
        .catch(() => {
          onError(`${t('microphone_access_denied')}.`, t('please_ensure_that_you_have_allowed_browser_access_to_your_microphone'));
        });
    },
    [audioInputId, audioInputEnabled, audioInputDevices, isMicrophonePermissionDenied],
  );

  const getLocalVideoTrack = useCallback(
    async (forceCreatedTrack = false) => {
      if (!videoInputEnabled && !forceCreatedTrack) {
        setVideoTrack(null);
        return Promise.resolve(null);
      }
      const hasSelectedVideoDevice = videoInputDevices.some((device) => videoInputId && device.value === videoInputId);

      const shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;

      const options = {
        ...(shouldAcquireVideo && {
          ...videoConstrains,
          ...(hasSelectedVideoDevice && { deviceId: { exact: videoInputId } }),
        }),
      };

      return Video.createLocalVideoTrack(options)
        .then((newTrack) => {
          if (!videoInputEnabled) {
            newTrack.disable();
          }
          setVideoTrack(newTrack);
          return newTrack;
        })
        .catch(() => {
          onError(`${t('camera_access_denied')}.`, t('please_ensure_that_you_have_allowed_browser_access_to_your_camera'));
        });
    },
    [videoInputId, videoInputDevices, videoInputEnabled, isCameraPermissionDenied],
  );

  const getLocalEnvironmentVideoTrack = useCallback(async () => {
    if (!videoInputEnabled) {
      return Promise.resolve(null);
    }
    const hasSelectedVideoDevice = environmentalVideoInputDevices.some((device) => environmentVideoInputId && device.value === environmentVideoInputId);

    const options = {
      ...videoConstrains,
      ...(hasSelectedVideoDevice ? { deviceId: { exact: environmentVideoInputId } } : { facingMode: 'environment' }),
    };

    return Video.createLocalVideoTrack(options).then((newTrack) => {
      setVideoTrack(newTrack);
      return newTrack;
    });
  }, [environmentVideoInputId, videoInputDevices, videoInputEnabled, isCameraPermissionDenied]);

  const removeLocalAudioTrack = useCallback(() => {
    if (audioTrack) {
      audioTrack.stop();
      setAudioTrack(undefined);
    }
  }, [audioTrack]);

  const removeLocalVideoTrack = useCallback(() => {
    if (videoTrack) {
      videoTrack.stop();
      setVideoTrack(undefined);
    }
  }, [videoTrack]);

  const getAudioAndVideoTracks = useCallback(async () => {
    // if (!hasAudioInputDevices && !hasVideoInputDevices) return Promise.resolve();
    // if (isAcquiringLocalTracks || audioTrack || videoTrack) return Promise.resolve();

    setIsAcquiringLocalTracks(true);
    return Promise.all([getLocalAudioTrack(true), getLocalVideoTrack(true), getLocalDataTrack()]) // Video.createLocalTracks(localTrackConstraints)
      .then((tracks) => {
        const filteredTracks = tracks.filter((track) => track);
        return filteredTracks;
      })
      .finally(() => setIsAcquiringLocalTracks(false));
  }, [
    audioTrack,
    videoTrack,
    isAcquiringLocalTracks,
    audioInputDevices,
    videoInputDevices,
    hasAudioInputDevices,
    hasVideoInputDevices,
    isCameraPermissionDenied,
    isMicrophonePermissionDenied,
    audioInputId,
    videoInputId,
    environmentVideoInputId,
    videoInputEnabled,
    audioInputEnabled,
  ]);

  const localTracks = [audioTrack, videoTrack, dataTrack].filter((track) => track);

  return {
    localTracks,
    getLocalVideoTrack,
    getLocalEnvironmentVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  };
}
